/**
 * TrackSubcribeForm
 * 
 * @Author: Focci
 * @Date: 2023-08-17 21:34:14
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-17 21:34:14
 */

import PropTypes from 'prop-types'
import StyledInput from '@comp/form/StyledInput'
import StyledSelect from '@comp/form/StyledSelect'
import GroupedRadio from '@comp/form/GroupedRadio'
import useMessage from '@hook/useMessage'
import useApp from '@hook/useApp'
import { fetchPost, fetchProxy } from '@lib/fetch/client'
import { Button } from '@comp/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import useLogin from '@hook/useLogin'

const defaultParams = {
  trackType: 6,
  email: '',
  distance: 1,
}

export default function TrackSubcribeForm({
  property,
  data = [],
  types = [],
  onSuccess = () => {},
}) {
  const tt = useTranslations('track')
  const tc = useTranslations('common')
  const te = useTranslations('enquiry')

  const { user } = useLogin()
  const { setSubcribes } = useApp()
  const { message } = useMessage()
  
  const typeOptions = types.map((item) => ({ label: item.label, value: item.id }))
  const distanceOption = data.map((value) => ({
    label: `${value}${tt('kilometre')}`,
    value: String(value)
  }))

  const [param, setParam] = useState({ ...defaultParams })
  const [loading, setLoading] = useState(false)

  // 验证邮件
  const emailValid = useMemo(
    () => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(param.email), 
    [param.email]
  )

  // 保存
  const handleSubmit = useCallback(async () => {
    const { id } = property
    fetchProxy(
      fetchPost(`property-claim/${id}`, param),
      setLoading
    ).then(() => {
      onSuccess({ nextStep: 'success', param })
      // 更新状态
      setSubcribes((prev) => [id, ...prev.filter((f) => f !== id)])
    }).catch((err) => message(err))
  }, [property, param, onSuccess, setSubcribes, message])

  const handleChange = useCallback((e) => {
    setParam((prev) => ({ ...prev, ...e }))
  }, [])

  useEffect(() => {
    handleChange({ 
      ...defaultParams,
      email: user?.email || '',
      trackType: property?.track_type || 6
    })
  }, [handleChange, data, user, property?.track_type])

  return (
    <div className="my-8 flex flex-col gap-y-4 w-[30rem] mx-auto max-w-full">
      <div>
        <div className="whitespace-nowrap text-f.8 mb-1">
          {tt('confirmYourEmail')}
        </div>
        <GroupedRadio
          options={typeOptions}
          value={param.trackType}
          onChange={(e) => handleChange({ 
            trackType: parseInt(e.target.value, 10) 
          })}
          sx={{ 
            '&.MuiFormGroup-root': { 
              flexDirection: 'row'
            } 
          }}
        />
      </div>
      
      <div>
        <div className="whitespace-nowrap text-f.8 mb-1">
          {tt('confirmYourEmail')}
          <span className="text-error ml-1">*</span>
        </div>
        <StyledInput
          value={param.email}
          variant="outline"
          placeholder={te('typeYourEmail')}
          onChange={(e) => handleChange({ email: e.target.value })}
        />
      </div>
      <div>
        <div className="whitespace-nowrap text-f.8 mb-1">
          {tt('aroundDistance')}
        </div>
        <div className="max-w-[20rem]">
          <StyledSelect 
            options={distanceOption}
            value={param.distance}
            onChange={(e) => handleChange({ distance: e.target.value })}
          />
        </div>
      </div>

      <div className="mt-4 text-f.8">
        <div>{`${tt('receiveRelevantInformation', { distance: param.distance })}: `}</div>
        <div className="mt-2">{`- ${tt('latestValuationProperty')}`}</div>
        <div>{`- ${tt('latestOnSaleProperty')}`}</div>
        <div>{`- ${tt('latestTranscactionProperty')}`}</div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-4">
        <Button 
          size="large" 
          color="lightButton" 
          onClick={() => setParam({ ...defaultParams })}
        >
          <span className="text-f.8">{tc('reset')}</span>
        </Button>
        <Button 
          size="large" 
          color="primary" 
          disabled={!emailValid || loading}
          onClick={handleSubmit}
        >
          <span className="px-8 text-f.8">
            {loading ? 'Loading...' : tt('trackConfirm')}
          </span>
        </Button>
      </div>
    </div>
  )
}

TrackSubcribeForm.propTypes = {
  property: PropTypes.object,
  data: PropTypes.array,
  types: PropTypes.array,
  onSuccess: PropTypes.func,
}
